import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';

import { FgPriceDetails } from '@/components';
import { iconEye } from '@/components/icons';
import { global, GlobalComponentType } from '@/context';
import {
  ConfigurationContextType,
  GlobalConfigurationContext,
} from '@/context/context.configuration';
import {
  BREAKPOINT_LARGE_PX,
  BREAKPOINT_MEDIUM_PX,
  BREAKPOINT_SMALL_PX,
} from '@/settings';
import { baseStyles, buttonStyles, skewedLabelStyles } from '@/styles';
import { Mode, Variant } from '@/types';
import { formatDate } from '@/utils';

import '@/components/price-details/fg-price-details';
import './fg-configuration-date';
import './fg-configuration-detail-button';

@customElement('fg-configuration-summary')
@localized()
export class FgConfigurationSummary extends LitElement {
  fgPriceDetailsDialogRef: Ref<FgPriceDetails> = createRef();

  /**
   * Property to indicate if the price should be shown in the summary
   * @property showTotals
   */
  @property({ type: Boolean })
  removeTotals = false;

  @property({ type: Boolean })
  toggleable = false;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: GlobalConfigurationContext })
  configurationContext!: ConfigurationContextType;

  render() {
    const { layout, locale } = this.globalContext;
    const { mode, modalInset, removeTotals, variant, configurationData } =
      this.configurationContext;

    const { shipping, priceBreakdown, totalPrice } = configurationData;

    return html`
      <div>
        ${this.toggleable &&
        layout === 'wholesale' &&
        variant === Variant.DEFAULT
          ? html`
              <div class="button-wrapper">
                <fg-configuration-detail-button
                  part=${layout}
                ></fg-configuration-detail-button>
              </div>
            `
          : nothing}
        ${!removeTotals
          ? html`
              <div
                class="row ${classMap({
                  'modal-inset': modalInset && variant !== Variant.SIMPLE,
                })}"
              >
                ${shipping?.actualShippingDate
                  ? html`
                      <div class="shipped-and-track-trace-container">
                        <div class="shipped-container">
                          <span>${t('labels.shippedOn')}:</span>
                          <span class="shipped-on-date">
                            ${formatDate(
                              shipping.actualShippingDate,
                              locale,
                              'short',
                            )}
                          </span>
                        </div>
                        ${shipping?.trackingUrl && shipping?.trackingUrl !== ''
                          ? html`
                              <a
                                class="track-and-trace-button"
                                href=${shipping.trackingUrl}
                                target="_blank"
                              >
                                ${t('labels.trackAndTraceOrder')}
                              </a>
                            `
                          : nothing}
                      </div>
                    `
                  : nothing}
                ${variant === Variant.DEFAULT
                  ? html`
                      <fg-configuration-date
                        part=${layout}
                      ></fg-configuration-date>
                    `
                  : nothing}
                ${variant === Variant.SIMPLE
                  ? nothing
                  : html`
                      <div class="group">
                        ${totalPrice
                          ? html`
                              <div>
                                <span>
                                  ${t(
                                    mode === Mode.DEFAULT
                                      ? 'labels.allInPrice'
                                      : 'labels.indicationPrice',
                                  )}
                                </span>
                                <span class="total">
                                  ${totalPrice.excludingVat.formatted || '-'}
                                </span>
                              </div>
                              ${totalPrice.includingVat && layout === 'retail'
                                ? html`
                                    <div>
                                      <span>${t('labels.incVat')}</span>
                                      <span>
                                        ${totalPrice.includingVat.formatted ||
                                        '-'}
                                      </span>
                                    </div>
                                  `
                                : nothing}
                            `
                          : nothing}
                        ${!modalInset
                          ? html`
                              <div class="price-details">
                                <button
                                  class="button-link"
                                  @click=${this.openPriceDetailsDialog}
                                >
                                  ${layout === 'wholesale'
                                    ? html`
                                        <span class="icon-price-details">
                                          ${iconEye}
                                        </span>
                                      `
                                    : nothing}
                                  ${t('actions.viewPriceDetails')}
                                </button>
                              </div>
                            `
                          : nothing}
                      </div>
                    `}
              </div>
              <fg-price-details
                ${ref(this.fgPriceDetailsDialogRef)}
                .priceBreakdown=${priceBreakdown}
              ></fg-price-details>
            `
          : nothing}
      </div>
    `;
  }

  openPriceDetailsDialog() {
    this.fgPriceDetailsDialogRef.value?.openDialog();
  }

  static styles = [
    baseStyles,
    buttonStyles,
    skewedLabelStyles,
    css`
      :host {
        display: block;
      }

      .summary-left {
        display: flex;
        flex-direction: column;
      }

      .row {
        padding-top: 10px;
      }

      .row.hidden {
        display: none;
      }

      :host([part='retail']) .row:not(:first-child) {
        border-top: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      .total {
        font-family: var(
          --fg-font-family--brand,
          var(--fg-fallback-font-family--brand)
        );
        font-size: 1.125rem;
        font-weight: 700;
      }

      .price-details {
        font-size: 0.875rem;
        display: flex;
        justify-content: flex-end;
        margin-top: 10px;
      }

      .button-wrapper {
        display: flex;
        align-items: center;
      }

      .button-wrapper button {
        background: red;
      }

      .button-wrapper::after {
        content: '';
        display: flex;
        flex-grow: 1;
        margin-left: 10px;
        background: var(--fg-gray--300, var(--fg-fallback-gray--300));
        height: 1px;
      }

      .button-link,
      .button-link span {
        display: flex;
      }

      .button-link {
        align-items: center;
        gap: 5px;
      }

      .expected-delivery-date {
        display: flex;
        gap: 4px;
      }

      .expected-delivery-container {
        display: inline-flex;
        gap: 4px;
      }

      .shipped-and-track-trace-container,
      .expected-date-container {
        display: flex;
        flex-direction: row;
        justify-content: center;
        position: relative;
        margin-bottom: 16px;
        @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
          && {
            flex-direction: column;
          }
        }
      }

      .expected-date-container {
        margin-bottom: 0;
      }

      :host([part='retail']) .expected-date-container {
        width: auto;

        @media screen and (min-width: ${BREAKPOINT_LARGE_PX}) {
          && {
            width: 100%;
          }
        }

        & .shipping-date > div span {
          width: auto;
        }
      }

      .shipped-container {
        text-align: left;
        display: flex;
        align-items: center;
      }

      .shipped-on-date {
        margin-left: 4px;
      }

      :host([part='retail']) .track-and-trace-button {
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
        background-color: var(--fg-tertiary, var(--fg-fallback-tertiary));
        font-size: 1.125rem;
      }

      :host([part='wholesale']) .track-and-trace-button {
        font-size: var(--fg-font-size--xl);
      }

      .track-and-trace-button {
        background-color: var(
          --fg-tertiary--dark,
          var(--fg-fallback-tertiary--dark)
        );
        color: var(--fg-secondary, var(--fg-fallback-secondary));
        display: flex;
        align-items: center;
        justify-content: center;
        height: fit-content;
        padding: 11.5px 24px 11.5px 24px;
        border-radius: 48px;
        text-decoration: none;
        font-family: var(
          --fg-font-family--brand,
          var(--fg-fallback-font-family--brand)
        );
        font-weight: 600;
        @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
          && {
            margin-top: 9px;
            width: max-content;
          }
        }
      }

      @media screen and (max-width: ${BREAKPOINT_SMALL_PX - 1}px) {
        .price-details {
          justify-content: flex-end;
        }

        .row div:not(.group):not(.expected-date-container):not(.price-details) {
          display: flex;
          justify-content: space-between;
        }

        .row {
          display: flex;
          flex-direction: column-reverse;
          gap: 16px;
        }

        .shipped-and-track-trace-container:has(.shipped-container) {
          flex-direction: column;
          margin-bottom: 0px;
        }

        .row div.expected-date-container {
          display: flex;
          flex-direction: column;
        }

        .group {
          padding-top: 5px;
        }
      }

      @media screen and (max-width: ${BREAKPOINT_MEDIUM_PX - 1}px) {
        fg-configuration-date {
          align-self: flex-start;
          width: 100%;
        }

        .modal-inset.row div.expected-date-container {
          display: none;
        }

        .group {
          width: 100%;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        :host {
          font-size: 1rem;
        }

        .row {
          display: flex;
          justify-content: space-between;
        }

        .row
          > div:not(.shipped-container):not(
            .shipped-and-track-trace-container
          ):not(.expected-date-container),
        .row.setup-price > div:only-child {
          text-align: right;
          flex-grow: 1;
        }

        .row > div:first-child {
          text-align: left;
        }

        .row div:last-child span:last-child:not(.icon-price-details) {
          width: 125px;
          display: inline-block;
        }

        .row:not(.modal-inset)
          div:last-child
          span:last-child:not(.icon-price-details) {
          padding-right: 1.25rem;
        }

        .total {
          font-size: 1.375rem;
        }

        .price-details {
          padding-right: 1.25rem;
        }
      }
    `,
  ];
}
